import React from 'react';
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Pencil, Trash2, PlusCircle, ShoppingBag, ChevronDown, ChevronUp } from "lucide-react";
import { Product } from '../../types/product';

interface AdminInventoryMobileProps {
  data: Product[] | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  onDeleteProduct: (productId: string) => void;
  onEditProduct: (productId: string, product: Product) => void;
}

const AdminInventoryMobile: React.FC<AdminInventoryMobileProps> = ({
  data,
  isLoading,
  error,
  refetch,
  onDeleteProduct,
  onEditProduct
}) => {

  const navigate = useNavigate();
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);

  const toggleProductExpand = (productId: string) => {
    setExpandedProductId(expandedProductId === productId ? null : productId);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-10 h-10 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-3 text-sm text-gray-600">Loading inventory...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-6 flex items-center justify-center">
        <div className="w-full max-w-sm bg-white rounded-lg shadow-sm p-6 text-center">
          <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-2xl">!</span>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Unable to load products</h2>
          <p className="text-sm text-gray-600 mb-4">Please check your connection and try again</p>
          <button
            onClick={() => refetch()}
            className="w-full px-4 py-2.5 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasNoProducts = data?.length === 0;

  if (hasNoProducts) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-6 flex items-center justify-center">
        <div className="w-full max-w-sm bg-white rounded-lg shadow-sm p-6 text-center">
          <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="h-8 w-8 text-blue-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Start Your Inventory</h2>
          <p className="text-sm text-gray-600 mb-6">
            Add your first product to begin managing your inventory
          </p>
          <button
            onClick={() => navigate("/admin/add-product")}
            className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors inline-flex items-center justify-center gap-2 font-medium"
          >
            <PlusCircle className="w-5 h-5" />
            <span>Add First Product</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 bg-gray-50">
      {/* Sticky Header */}
      <div className="top-0 z-10 px-4 py-3 flex justify-end border-gray-100">
        <button
          onClick={() => navigate("/admin/add-product")}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          <PlusCircle className="w-4 h-4 mr-1.5" />
          Add Product
        </button>
      </div>

      {/* Product List */}
      <div className="space-y-2.5">
        {data?.map((product) => (
          <div
            key={product.id}
            className="bg-white rounded overflow-hidden"
          >
            <div
              className="p-4 flex items-start gap-3"
              onClick={() => toggleProductExpand(product.id)}
            >
              <div className="h-16 w-16 rounded bg-gray-50 overflow-hidden flex-shrink-0">
                <img
                  className="h-full w-full object-cover"
                  src={product.images[0]}
                  alt={product.title}
                  onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = "https://via.placeholder.com/64?text=No+Image";
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-medium text-gray-900 truncate line-clamp-2">
                    {product.title}
                  </h3>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleProductExpand(product.id);
                    }}
                    className="flex-shrink-0 p-1 -mr-1 rounded-full hover:bg-gray-50"
                  >
                    {expandedProductId === product.id ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    ₹{(product.mrp || 0).toLocaleString()}
                  </span>
                  {(product.discountPercent || 0) > 0 && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700">
                      {product.discountPercent}% OFF
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium
                    ${(product.stock || 0) > 10
                        ? "bg-green-50 text-green-700"
                        : (product.stock || 0) > 0
                          ? "bg-yellow-50 text-yellow-700"
                          : "bg-red-50 text-red-700"
                      }`}
                  >
                      {(product.stock || 0) === 0
                      ? "Out of stock"
                      : (product.stock || 0) <= 10
                        ? `Low stock (${product.stock || 0})`
                        : `${product.stock || 0} in stock`}
                  </span>
                </div>
              </div>
            </div>

            {expandedProductId === product.id && (
              <div className="px-4 py-3 bg-gray-50">
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {product.description || "No description available"}
                  </p>
                </div>
                <div className="space-y-2">
                  <button
                    onClick={() => onEditProduct(product.id, product)}
                    className="w-full inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit Product
                  </button>
                  <button
                    onClick={() => onDeleteProduct(product.id)}
                    className="w-full inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Product
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminInventoryMobile;